import type { UrlDto } from "@reactive-resume/dto";
import { resolveTemplate } from "@reactive-resume/utils";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useReducer, useState } from "react";

import { RESUME_PREVIEW_KEY } from "@/client/constants/query-keys";
import { templateCardUrl, templateImageUrl } from "@/client/constants/template-images";
import { axios } from "@/client/libs/axios";

export const previewResume = async (data: { id: string }) => {
  const response = await axios.get<UrlDto>(`/resume/print/${data.id}/preview`, {
    skipErrorToast: true,
  });

  return response.data;
};

export const useResumePreview = (id: string) => {
  const {
    error,
    isPending: loading,
    data,
  } = useQuery({
    queryKey: [RESUME_PREVIEW_KEY, { id }],
    queryFn: () => previewResume({ id }),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  return { url: data?.url, loading, error };
};

export const addVersionToUrl = (url: string, version: string) => {
  const separator = url.includes("?") ? "&" : "?";

  return `${url}${separator}v=${encodeURIComponent(version)}`;
};

// Aperçus déjà téléchargés pendant la session : affichés sans attente au retour sur la page.
const loadedPreviews = new Set<string>();

/**
 * Miniature d'un CV : vignette du modèle tout de suite, puis l'aperçu réel une fois
 * entièrement téléchargé, pour ne jamais afficher de carte blanche.
 */
export const useResumeThumbnail = (id: string, updatedAt: unknown, template: unknown) => {
  const { url } = useResumePreview(id);
  const generated = url && id ? addVersionToUrl(url, String(updatedAt)) : "";
  const card = templateCardUrl(resolveTemplate(template));
  const [fallback, setFallback] = useState(card);
  const [broken, setBroken] = useState("");
  const [, refresh] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    setFallback(card);
  }, [card]);

  useEffect(() => {
    if (!generated || loadedPreviews.has(generated)) return;
    let active = true;
    const image = new Image();
    image.addEventListener("load", () => {
      loadedPreviews.add(generated);
      if (active) refresh();
    });
    image.src = generated;
    return () => {
      active = false;
    };
  }, [generated]);

  const src =
    generated && generated !== broken && loadedPreviews.has(generated) ? generated : fallback;

  const showTemplate = () => {
    if (src === generated) setBroken(generated);
    else if (src === card) setFallback(templateImageUrl(resolveTemplate(template)));
  };

  return { src, showTemplate };
};
